import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './FloatingGameButton.css';
import GameModal from '../GameModal/GameModal';
import { gameAPI } from '../../services/api';

interface UserSession {
  currentGame: string | null;
  isOnCooldown: boolean;
  cooldownEndsAt: string | null;
  windowEndsAt: string | null;
}

const FloatingGameButton: React.FC = () => {
  const { t } = useTranslation();
  const [showGameModal, setShowGameModal] = useState(false);
  const [currentDailyGame, setCurrentDailyGame] = useState<any>(null);
  const [gameId, setGameId] = useState<string>('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [canPlay, setCanPlay] = useState(true);
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [countdown, setCountdown] = useState<string>('');

  // Форматирование оставшегося времени
  const formatTimeRemaining = useCallback((endTime: string): string => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const diff = end - now;

    if (diff <= 0) return '';

    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  }, []);

  // Обновление countdown таймера
  useEffect(() => {
    if (!userSession?.windowEndsAt) {
      setCountdown('');
      return;
    }

    const updateCountdown = () => {
      const remaining = formatTimeRemaining(userSession.windowEndsAt!);
      setCountdown(remaining);

      // Если время вышло - перезагружаем данные
      if (!remaining) {
        loadGameData();
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [userSession?.windowEndsAt, formatTimeRemaining]);

  const loadGameData = async () => {
    try {
      const response = await gameAPI.getAllGames();
      if (response.data.success) {
        // Обрабатываем userSession
        if (response.data.userSession) {
          setUserSession(response.data.userSession);
        }

        if (response.data.games.length > 0) {
          const dailyGame = response.data.games.find((game: any) => game.isCurrentGame);
          if (dailyGame) {
            setCurrentDailyGame(dailyGame);
            setGameId(dailyGame.id);
            setCanPlay(dailyGame.canPlay);

            // Анимация при доступности игры
            if (dailyGame.canPlay && !canPlay) {
              setIsAnimating(true);
              setTimeout(() => setIsAnimating(false), 3000);
            }
          } else {
            // Если нет текущей игры - берём первую
            const firstGame = response.data.games[0];
            setCurrentDailyGame(firstGame);
            setGameId(firstGame.id);
            setCanPlay(true);
          }
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки данных мини-игр:', error);
      // При ошибке всё равно показываем кнопку
      setCanPlay(true);
    }
  };

  useEffect(() => {
    loadGameData();
    // Обновляем каждые 10 секунд (чаще для отслеживания кулдауна)
    const interval = setInterval(loadGameData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleClick = () => {
    setShowGameModal(true);
  };

  // Если на кулдауне - НЕ показываем кнопку
  if (userSession?.isOnCooldown) {
    return null;
  }

  // Если кнопка показывается (не на кулдауне) - она всегда активна (синяя)
  // Серая только когда есть специальное ограничение (например, все попытки использованы)
  const isButtonActive = !userSession?.isOnCooldown;

  return (
    <>
      <button
        className={`floating-game-button ${isButtonActive ? 'can-play' : 'disabled'} ${isAnimating ? 'animating' : ''}`}
        onClick={handleClick}
        title={currentDailyGame?.name || t('games.miniGames')}
      >
        <span className="game-icon">🎮</span>
        <span className="play-indicator">
          <span className="play-dot"></span>
        </span>
        {isAnimating && (
          <span className="new-game-badge">NEW!</span>
        )}
        {/* Показываем таймер 5-минутного окна */}
        {countdown && (
          <span className="window-timer">{countdown}</span>
        )}
      </button>

      <GameModal
        isOpen={showGameModal}
        onClose={() => {
          setShowGameModal(false);
          loadGameData();
        }}
        gameId={gameId}
      />
    </>
  );
};

export default FloatingGameButton;
